/* ==========================================================================
   BOSTAN CUSTOMIZATION — bostan-scroll-nav.js
   Hide-on-scroll header for Symmetry 8.0.0 (Clean Canvas).

   Pairs with: assets/bostan-scroll-nav.css
   Wired from: layout/theme.liquid  (single <script defer>, marked BOSTAN CUSTOMIZATION)

   BEHAVIOUR
     - scroll down  -> header slides up out of view
     - scroll up    -> header slides back in
     - near the top -> header is always visible
     - never hides while a header overlay/menu/drawer/modal is open, while
       keyboard focus is inside the header, or while the theme editor has the
       header selected

   DESIGN NOTES
     - Only ever toggles two classes on `.section-header`: `bostan-nav` (feature
       active) and `bostan-nav--hidden` / `bostan-nav--locked` (state). All
       animation lives in CSS.
     - Activates ONLY when the header computes to `position: sticky`, i.e. it
       respects the merchant's "Enable sticky header" setting. If the merchant
       turns sticky off, this file does nothing.
     - Window-level listeners (scroll/resize/keydown/pointerdown/focus) are
       bound EXACTLY ONCE at module scope. Section reloads in the theme editor
       re-resolve the header element via attach() instead of re-binding, so
       duplicate listeners are impossible.
     - Scroll handling is passive + rAF-coalesced; the only per-tick work is one
       `window.scrollY` read and some arithmetic. The (slightly) more expensive
       lock probing happens only when a state change is actually about to occur.

   See the CRITICAL note at the top of bostan-scroll-nav.css before changing how
   the transform is applied — `.main-search` is a `position: fixed` descendant
   of the transformed element.
   ========================================================================== */

(function () {
  'use strict';

  // Singleton guard: a second copy of this file (e.g. re-injected by a section
  // render in the theme editor) must not build a second controller.
  if (window.BostanScrollNav) return;

  var CONFIG = {
    // Cumulative px in one direction before we act.
    //
    // These are deliberately asymmetric and deliberately NOT tiny. Hiding costs
    // the user their navigation, so it demands a committed downward gesture
    // (~one wheel notch); revealing is what the user wants back, so it is
    // eager. The gap between the two is the hysteresis band: an oscillation of
    // up to hideDelta px cannot toggle the header, which is what stops a shaky
    // scroll or a momentum wobble from strobing it.
    hideDelta: 80,
    showDelta: 30,
    // A single-frame jump larger than this is treated as programmatic (anchor
    // link, tab scroll-into-view, scroll restoration) rather than a gesture.
    // Kept well above the fastest realistic fling (~500-800px/frame) so that
    // genuinely fast scrolling still hides the header instead of being
    // misread as a jump.
    jumpDelta: 1000,
    // Gap after which the direction accumulator is considered stale. A real
    // scroll gesture fires events every frame or so; anything separated by
    // longer than this is a fresh gesture, not a continuation. Without it, a
    // handful of tiny nudges seconds apart creep past the threshold and the
    // header hides when the user has barely moved.
    idleReset: 300,
    // Minimum size of the always-visible zone at the top of the page, used if
    // the header measures smaller than this.
    minTopZone: 64,
    resizeDebounce: 250,
    // Quiet window after a resize or a fresh attach. Reflow (breakpoint change,
    // late fonts/images) shifts the scroll position on its own and fires scroll
    // events that are not user intent; acting on them re-hides a header we just
    // revealed. Track position during this window, but change no state.
    settleMs: 400
  };

  var SELECTOR_HEADER = '.section-header';

  // Candidate overlays that must pin the header while open. These are only
  // CANDIDATES — every match is then checked for being genuinely open and
  // rendered by isOverlayShown().
  //
  // NOTE: do NOT reduce this to a bare `[aria-modal="true"]`. Symmetry renders
  // <cart-drawer role="dialog" aria-modal="true" aria-hidden="true"> into the
  // markup permanently, open or not, so a bare match pins the header forever
  // and the feature silently never engages.
  var SELECTOR_OVERLAY_CANDIDATES = [
    'dialog[open]',
    'side-drawer[open]',
    'cart-drawer[open]',
    '.js-cart-drawer[open]',
    '[aria-modal="true"]:not([aria-hidden="true"])'
  ].join(',');

  // Symmetry's add-to-cart notification is appended INTO `.pageheader--sticky`
  // (main.js:2977) and is absolutely positioned below the header, so the header
  // must stay put while it is on screen.
  var SELECTOR_NOTIFICATION =
    '.added-notification:not(.added-notification--hidden):not(.added-notification--dismissed)';

  var state = {
    header: null,
    observer: null,
    enabled: false,
    hidden: false,
    lastY: 0,
    accum: 0,
    dir: 0,
    lastEventAt: -Infinity,
    settleUntil: 0,
    ticking: false,
    keyboardMode: false,
    focusInHeader: false,
    designSelected: false
  };

  /* ---------------------------------------------------------------------
     Helpers
     --------------------------------------------------------------------- */

  function maxScroll() {
    return Math.max(
      0,
      document.documentElement.scrollHeight - window.innerHeight
    );
  }

  // Clamping neutralises iOS Safari / Android overscroll ("rubber band"), which
  // reports scrollY < 0 at the top and > maxScroll at the bottom. Without this,
  // a bounce reads as a direction change and flickers the header.
  function currentY() {
    var y = window.scrollY || window.pageYOffset || 0;
    if (y < 0) return 0;
    var max = maxScroll();
    return y > max ? max : y;
  }

  function topZone() {
    var h = state.header ? state.header.offsetHeight : 0;
    return Math.max(CONFIG.minTopZone, h);
  }

  function isSticky(el) {
    var pos = window.getComputedStyle(el).position;
    return pos === 'sticky' || pos === '-webkit-sticky';
  }

  /** An element counts as an open overlay only if it is actually rendered. */
  function isOverlayShown(el) {
    if (el.getAttribute('aria-hidden') === 'true') return false;
    var cs = window.getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden') return false;
    return el.getClientRects().length > 0;
  }

  function computeOverlayOpen() {
    var header = state.header;
    if (header && header.querySelector(SELECTOR_NOTIFICATION)) return true;

    var nodes = document.querySelectorAll(SELECTOR_OVERLAY_CANDIDATES);
    for (var i = 0; i < nodes.length; i += 1) {
      if (isOverlayShown(nodes[i])) return true;
    }
    return false;
  }

  // The overlay sweep is the only non-O(1) part of the lock check, so its result
  // is cached. The MutationObserver invalidates it the instant an `open` /
  // `aria-hidden` / `aria-modal` attribute changes anywhere, and the time gate
  // bounds staleness for overlays that appear by insertion rather than by
  // attribute change (e.g. app-injected popups).
  var overlayCache = { at: -Infinity, val: false };
  var OVERLAY_TTL = 200;

  function now() {
    return (window.performance && window.performance.now)
      ? window.performance.now()
      : new Date().getTime();
  }

  function invalidateOverlayCache() {
    overlayCache.at = -Infinity;
  }

  function hasOpenOverlay() {
    var t = now();
    if (t - overlayCache.at < OVERLAY_TTL) return overlayCache.val;
    overlayCache.at = t;
    overlayCache.val = computeOverlayOpen();
    return overlayCache.val;
  }

  /**
   * O(1) portion of the lock check — runs on every scroll tick.
   * Mirrors the selector list in the CSS "overlay guard" block.
   */
  function isLockedCheap() {
    var header = state.header;
    if (!header) return true;

    var b = document.body;
    if (
      b.classList.contains('show-search') ||
      b.classList.contains('reveal-mobile-nav') ||
      b.classList.contains('enable-mobile-nav-transition') ||
      b.classList.contains('overflow-hidden')
    ) {
      return true;
    }

    if (
      header.classList.contains('section-header--nav-open') ||
      header.classList.contains('section-header--nav-hover')
    ) {
      return true;
    }

    return !!(state.focusInHeader || state.designSelected);
  }

  /** True when something is open that must keep the header pinned. */
  function isLocked() {
    return isLockedCheap() || hasOpenOverlay();
  }

  /* ---------------------------------------------------------------------
     State application (idempotent — only writes when something changes, so the
     MutationObserver below cannot feed back into itself)
     --------------------------------------------------------------------- */

  function reveal() {
    if (!state.header || !state.hidden) return;
    state.hidden = false;
    state.header.classList.remove('bostan-nav--hidden');
  }

  function hide() {
    if (!state.header || state.hidden) return;
    state.hidden = true;
    state.header.classList.add('bostan-nav--hidden');
  }

  function setLocked(locked) {
    if (!state.header) return;
    if (locked) reveal();
    if (state.header.classList.contains('bostan-nav--locked') !== locked) {
      state.header.classList.toggle('bostan-nav--locked', locked);
    }
  }

  function resetTracking() {
    state.accum = 0;
    state.dir = 0;
    state.lastEventAt = -Infinity;
    state.lastY = currentY();
    state.settleUntil = now() + CONFIG.settleMs;
  }

  /* ---------------------------------------------------------------------
     Scroll
     --------------------------------------------------------------------- */

  function update() {
    state.ticking = false;
    if (!state.enabled || !state.header) return;

    var y = currentY();
    var diff = y - state.lastY;
    state.lastY = y;

    if (diff === 0) return;

    if (isLocked()) {
      setLocked(true);
      state.accum = 0;
      state.dir = 0;
      return;
    }
    setLocked(false);

    // Post-resize / post-attach settling: reflow moves the scroll position by
    // itself. Keep tracking, change nothing.
    if (now() < state.settleUntil) {
      state.accum = 0;
      state.dir = 0;
      return;
    }

    // Programmatic jump (anchor, scroll restoration, tab scroll-into-view):
    // show the header rather than guessing intent.
    if (Math.abs(diff) > CONFIG.jumpDelta) {
      reveal();
      state.accum = 0;
      state.dir = 0;
      return;
    }

    // Always visible near the top.
    if (y <= topZone()) {
      reveal();
      state.accum = 0;
      state.dir = 0;
      return;
    }

    var dir = diff > 0 ? 1 : -1;
    var t = now();
    if (dir !== state.dir || t - state.lastEventAt > CONFIG.idleReset) {
      // Direction changed, or the previous movement was too long ago to be the
      // same gesture. Restarting the accumulator here is the anti-flicker
      // mechanism: a wobble mid-gesture, or a few stray nudges spread over
      // seconds, never reach the threshold.
      state.dir = dir;
      state.accum = 0;
    }
    state.lastEventAt = t;
    state.accum += Math.abs(diff);

    if (dir === 1) {
      if (state.accum >= CONFIG.hideDelta) hide();
    } else if (state.accum >= CONFIG.showDelta) {
      reveal();
    }
  }

  function onScroll() {
    if (state.ticking) return;
    state.ticking = true;
    window.requestAnimationFrame(update);
  }

  /* ---------------------------------------------------------------------
     Resize
     --------------------------------------------------------------------- */

  var resizeTimer = null;
  function onResize() {
    if (resizeTimer) window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(function () {
      resizeTimer = null;
      // Sticky can be toggled by a breakpoint or by the merchant in the editor.
      if (state.header && state.enabled && !isSticky(state.header)) {
        detach();
        return;
      }
      if (!state.enabled) {
        attach();
        return;
      }
      reveal();
      resetTracking();
    }, CONFIG.resizeDebounce);
  }

  /* ---------------------------------------------------------------------
     Keyboard focus — the header must be visible when a keyboard user tabs into
     it. Gated on genuine keyboard interaction: Symmetry's own `body.tab-used`
     is add-only and never cleared, so a bare `:focus-within` rule would pin the
     header forever after the first Tab press of the session.
     --------------------------------------------------------------------- */

  function onKeydown(evt) {
    var k = evt.key;
    if (k === 'Tab' || k === 'Enter' || k === ' ' || k === 'Escape' ||
        (typeof k === 'string' && k.indexOf('Arrow') === 0)) {
      state.keyboardMode = true;
    }
  }

  function onPointerdown() {
    state.keyboardMode = false;
  }

  function onFocusin(evt) {
    var inHeader = !!(
      state.keyboardMode &&
      state.header &&
      state.header.contains(evt.target)
    );
    if (inHeader === state.focusInHeader) return;
    state.focusInHeader = inHeader;
    setLocked(isLocked());
  }

  function onFocusout(evt) {
    // relatedTarget === null means focus left the document / went to <body>.
    if (evt.relatedTarget) return;
    if (!state.focusInHeader) return;
    state.focusInHeader = false;
    setLocked(isLocked());
  }

  /* ---------------------------------------------------------------------
     Attach / detach — re-resolves the header element. Safe to call repeatedly.
     --------------------------------------------------------------------- */

  function detach() {
    if (state.observer) {
      state.observer.disconnect();
      state.observer = null;
    }
    if (state.header) {
      state.header.classList.remove(
        'bostan-nav',
        'bostan-nav--hidden',
        'bostan-nav--locked'
      );
    }
    state.header = null;
    state.enabled = false;
    state.hidden = false;
  }

  function attach() {
    detach();

    var header = document.querySelector(SELECTOR_HEADER);
    if (!header) return;

    // Respect the merchant's "Enable sticky header" setting — a non-sticky
    // header scrolls away on its own and needs nothing from us.
    if (!isSticky(header)) return;

    state.header = header;
    state.enabled = true;
    state.hidden = false;
    header.classList.add('bostan-nav');

    // React the moment an overlay opens, without waiting for a scroll tick.
    // reveal()/setLocked() are idempotent, so the observer cannot loop.
    state.observer = new MutationObserver(function () {
      invalidateOverlayCache();
      setLocked(isLocked());
    });
    // Narrow, non-subtree: body and header class flips (Symmetry's own states).
    state.observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });
    state.observer.observe(header, {
      attributes: true,
      attributeFilter: ['class']
    });
    // Document-wide but attribute-filtered to three rarely-changing attributes,
    // so this fires only when a drawer/dialog/modal actually opens or closes.
    state.observer.observe(document.documentElement, {
      subtree: true,
      attributes: true,
      attributeFilter: ['open', 'aria-hidden', 'aria-modal']
    });

    // A page can load already scrolled (reload, back/forward, deep link).
    // Start visible and only hide once the user actually scrolls down.
    resetTracking();
    invalidateOverlayCache();
    setLocked(isLocked());
  }

  /* ---------------------------------------------------------------------
     Bind once
     --------------------------------------------------------------------- */

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize, { passive: true });
  window.addEventListener('orientationchange', onResize, { passive: true });
  document.addEventListener('keydown', onKeydown, true);
  document.addEventListener('pointerdown', onPointerdown, true);
  document.addEventListener('focusin', onFocusin, true);
  document.addEventListener('focusout', onFocusout, true);

  // Symmetry closes overlays via these; re-evaluate promptly.
  document.addEventListener('on:cart:change', function () {
    invalidateOverlayCache();
    setLocked(isLocked());
  });

  // Theme editor: sections are torn down and re-rendered. Re-resolve rather
  // than re-bind. `attach()` fully cleans up the previous element first.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (evt) {
      if (evt.target && evt.target.classList.contains('section-header')) {
        attach();
      }
    });
    document.addEventListener('shopify:section:unload', function (evt) {
      if (evt.target && evt.target === state.header) detach();
    });

    // Keep the header on screen while the merchant is editing it.
    var setSelected = function (selected) {
      return function (evt) {
        if (!state.header) return;
        if (evt.target && !state.header.contains(evt.target) && evt.target !== state.header) return;
        state.designSelected = selected;
        setLocked(isLocked());
      };
    };
    document.addEventListener('shopify:section:select', setSelected(true));
    document.addEventListener('shopify:section:deselect', setSelected(false));
    document.addEventListener('shopify:block:select', setSelected(true));
    document.addEventListener('shopify:block:deselect', setSelected(false));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attach);
  } else {
    attach();
  }

  // Small public surface: doubles as the singleton flag and gives QC/debugging
  // a handle without exposing internals.
  window.BostanScrollNav = {
    attach: attach,
    detach: detach,
    reveal: reveal,
    isLocked: isLocked,
    state: state
  };
})();
